import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Inbox } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { pcmpRequestMineQueryOptions } from '../../api/pcmpRequests.api';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import {
  PCMP_REQUEST_EMPTY_MESSAGE,
  PCMP_REQUEST_STATUS_LABELS,
} from '../../constants/pcmpRequest.constants';
import type { PcmpRequestFilterController } from '../../hooks/usePcmpRequestFilters';
import {
  PCMP_REQUEST_STATUSES,
  type PcmpRequestMineItem,
  type PcmpRequestQueueParams,
  type PcmpRequestStatus,
} from '../../types/pcmpRequest.types';
import { PcmpRequestCategoryFilter } from './PcmpRequestCategoryFilter';
import { getPcmpRequestMineColumns } from './PcmpRequestColumns';
import { PcmpRequestRowActions } from './PcmpRequestRowActions';

const ALL_STATUSES = '__all__';

const statusItems = [
  { value: ALL_STATUSES, label: 'All statuses' },
  ...PCMP_REQUEST_STATUSES.map((value) => ({ value, label: PCMP_REQUEST_STATUS_LABELS[value] })),
];

interface PcmpRequestMineViewProps {
  controller: PcmpRequestFilterController;
  params: PcmpRequestQueueParams;
}

/** My Requests: what I raised, with where each request stands. */
export function PcmpRequestMineView({ controller, params }: PcmpRequestMineViewProps) {
  const router = useRouter();
  const { limit, requestStatus } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpRequestMineQueryOptions(params),
  );

  useKeepPageInRange(params.page, data?.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpRequestMineItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const rows = useMemo(() => data?.data ?? [], [data]);

  const columns = getPcmpRequestMineColumns({
    renderActions: (item) => <PcmpRequestRowActions item={item} />,
  });

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <PcmpRequestCategoryFilter
          value={controller.requestCategory}
          onChange={controller.setRequestCategory}
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Request status</span>
          <Select<string>
            items={statusItems}
            value={requestStatus ?? ALL_STATUSES}
            onValueChange={(next) =>
              controller.setRequestStatus(
                next === null || next === ALL_STATUSES ? undefined : (next as PcmpRequestStatus),
              )
            }
          >
            <SelectTrigger aria-label="Request status" className="h-9 w-44">
              <SelectValue />
              <SelectIcon />
            </SelectTrigger>
            <SelectContent>
              {statusItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load your requests"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage={PCMP_REQUEST_EMPTY_MESSAGE.MINE}
          skeletonRowCount={Math.min(limit, 15)}
          onRowClick={openProposal}
        />
      )}

      {data?.meta && !error ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
    </>
  );
}
